import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A titled card on a record page (W12 U2). The heading is the card's first
 * child, so "the heading's parent" is the whole card (the e2e specs find
 * sections that way). An optional count sits beside the title and an action
 * (usually a small + button) at the top right. Says "None" when empty.
 */
export function RecordCard({
  title,
  count,
  action,
  isEmpty = false,
  emptyText = "None",
  className,
  children,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  isEmpty?: boolean;
  emptyText?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section
      className={cn(
        "relative rounded-xl border bg-card px-5 py-4 text-card-foreground shadow-xs",
        className,
      )}
    >
      <h2 className="mb-3 flex items-center gap-2 pr-10 text-sm font-semibold">
        {title}
        {count !== undefined ? (
          <span className="rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">
            {count}
          </span>
        ) : null}
      </h2>
      {action ? <div className="absolute right-3 top-3">{action}</div> : null}
      {isEmpty ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        children
      )}
    </section>
  );
}

/** One row in a card list: a primary line, a muted secondary line, a trailing control. */
export function RecordRow({
  primary,
  secondary,
  trailing,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <li className="group flex items-start gap-2 py-2 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1 text-sm">
        <div className="truncate">{primary}</div>
        {secondary ? (
          <div className="truncate text-xs text-muted-foreground">
            {secondary}
          </div>
        ) : null}
      </div>
      {trailing ? (
        <div className="shrink-0 opacity-60 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {trailing}
        </div>
      ) : null}
    </li>
  );
}

/** Label / value pairs for an About card. */
export function PropertyList({
  items,
}: {
  items: { label: string; value: ReactNode }[];
}) {
  return (
    <dl className="grid grid-cols-[minmax(0,7rem)_1fr] gap-x-3 gap-y-2 text-sm">
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="truncate text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 break-words">{item.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
